import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { Sparkles, Loader2, ArrowUpRight, Inbox, Rocket, Layers, Radio, AlertTriangle, ChevronRight, Activity } from "lucide-react";

const SEV = { high: "var(--signal)", medium: "#d4a017", low: "#94a3b8" };
const fmt$ = (v) => `$${(v || 0).toLocaleString()}`;

export default function Dashboard() {
  const { ctx, user, activeTenant } = useAtlas();
  const navigate = useNavigate();
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [attention, setAttention] = useState(null);
  const [decisions, setDecisions] = useState(null);
  const [missions, setMissions] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [activity, setActivity] = useState([]);
  const hasEntities = (ctx?.entities || []).length > 0;

  useEffect(() => {
    api.get("/ai/attention").then((r) => setAttention(r.data)).catch(() => {});
    api.get("/platform/decisions").then((r) => setDecisions(r.data)).catch(() => {});
    api.get("/platform/missions").then((r) => setMissions(r.data.filter((m) => m.status !== "completed" && m.status !== "abandoned"))).catch(() => {});
    api.get("/platform/metrics").then((r) => setMetrics(r.data.metrics)).catch(() => {});
    api.get("/activity", { params: { limit: 8 } }).then((r) => setActivity(r.data)).catch(() => {});
    if (hasEntities) api.get("/platform/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx?.tenant?.id]);

  const genBrief = () => { setBriefLoading(true); api.get("/ai/brief").then((r) => setBrief(r.data)).catch(() => {}).finally(() => setBriefLoading(false)); };
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="atlas-home">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 rise">
        <div>
          <p className="eyebrow mb-1">{activeTenant?.name} · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900">{greet}, {user?.name?.split(" ")[0]}.</h1>
          <p className="text-sm text-slate-500 mt-1">{attention ? attention.summary : "Atlas is reading your business…"}</p>
        </div>
        <div className="flex gap-2">
          {metrics.slice(0, 4).map((m) => (
            <div key={m.name} className="hidden md:block px-3 py-2 rounded-xl bg-white/70 border text-right" style={{ borderColor: "var(--line)" }} data-testid={`home-metric-${m.name.toLowerCase().replace(/ /g, "-")}`}>
              <div className="font-heading text-lg font-semibold tabular leading-none" style={{ color: m.status === "critical" ? "var(--signal)" : m.status === "warning" ? "#b7791f" : "#0f172a" }}>{m.unit === "USD" ? fmt$(m.value) : m.value}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{m.name}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Brief */}
        <section className="lg:col-span-7 panel-ink grain p-6 rise rise-1 min-h-[280px]" data-testid="home-brief">
          <div className="relative flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><span className="ai-dot" /><span className="eyebrow-light">Executive brief · Atlas</span></div>
            <button onClick={genBrief} disabled={briefLoading} data-testid="generate-brief" className="text-xs font-medium px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center gap-1.5 disabled:opacity-60">
              {briefLoading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the business…</> : brief ? "Refresh" : <><Sparkles className="w-3.5 h-3.5" /> Generate brief</>}
            </button>
          </div>
          {!brief && !briefLoading && (
            <div className="relative">
              <h2 className="font-heading text-2xl font-medium text-white leading-snug max-w-lg">What changed, what matters, what needs you — synthesized across every application{hasEntities ? " and operating entity" : ""}.</h2>
              <p className="text-sm text-slate-400 mt-3 max-w-md">Atlas reads a permission-scoped snapshot of {activeTenant?.name}, then reasons over knowledge, standing orders and prior decisions.</p>
              <div className="flex flex-wrap gap-2 mt-6">
                {["What needs my attention today?", "Why is inventory at risk?", "Which deals are going stale?"].map((q) => (
                  <button key={q} onClick={() => navigate("/atlas", { state: { q } })} className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-200 hover:bg-white/10 transition-colors">{q}</button>
                ))}
              </div>
            </div>
          )}
          {brief && (
            <div className="relative space-y-4 rise">
              <p className="font-heading text-xl text-white leading-snug">{brief.headline}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <BriefList title="What matters" items={brief.matters} color="var(--signal)" />
                <BriefList title="Needs a decision" items={brief.decisions} color="#f59e0b" />
                <BriefList title="What changed" items={brief.changed} color="#94a3b8" />
                <BriefList title="Atlas is handling" items={brief.handling} color="var(--ai)" />
              </div>
            </div>
          )}
        </section>

        {/* Decisions */}
        <section className="lg:col-span-5 panel p-5 rise rise-2" data-testid="home-decisions">
          <Head icon={Inbox} title="Decisions waiting" count={decisions?.open?.length} to="/decisions" onGo={navigate} />
          <div className="space-y-1 mt-3">
            {decisions?.open?.length === 0 && <Empty text="Nothing needs your decision right now." />}
            {decisions?.open?.slice(0, 4).map((d) => (
              <button key={d.id} onClick={() => navigate("/decisions")} data-testid="home-decision" className="w-full text-left px-3 py-2.5 rounded-xl row-hover group">
                <div className="flex items-start gap-2.5"><span className="signal-dot mt-2 flex-shrink-0" />
                  <div className="flex-1 min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{d.title}</div><div className="text-xs text-slate-500 truncate">{d.recommends}</div></div>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-600 mt-1" /></div>
              </button>
            ))}
          </div>
          {decisions?.memory?.length > 0 && <p className="text-[11px] text-slate-400 mt-3 pt-3 hairline">Decision memory: {decisions.memory.length} prior decision(s) guide Atlas recommendations.</p>}
        </section>

        {/* Attention */}
        <section className="lg:col-span-5 panel p-5 rise rise-3" data-testid="home-attention">
          <Head icon={AlertTriangle} title="Needs attention" count={attention?.issues?.length} to="/atlas" onGo={navigate} />
          <div className="mt-3 space-y-1">
            {attention?.issues?.length === 0 && <Empty text="All clear across your applications." />}
            {attention?.issues?.slice(0, 5).map((i, k) => (
              <button key={k} onClick={() => i.link && navigate(i.link)} data-testid="home-attention-item" className="w-full text-left px-3 py-2 rounded-xl row-hover flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: SEV[i.severity] }} />
                <div className="flex-1 min-w-0"><div className="text-sm text-slate-800 truncate">{i.title}</div><div className="text-xs text-slate-500 truncate">{i.detail}</div></div>
                <span className="chip border-slate-200 text-slate-500 uppercase">{i.app}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Portfolio or Missions */}
        <section className="lg:col-span-7 panel p-5 rise rise-4" data-testid={hasEntities ? "home-portfolio" : "home-missions"}>
          {hasEntities && portfolio ? (<>
            <Head icon={Layers} title="Portfolio pulse" count={portfolio.entities.filter((e) => e.parent_id).length} to="/portfolio" onGo={navigate} />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
              {portfolio.entities.filter((e) => e.parent_id).map((e) => (
                <button key={e.id} onClick={() => navigate("/portfolio")} data-testid="home-entity-card" className="text-left p-3 rounded-xl border row-hover" style={{ borderColor: "var(--line)" }}>
                  <div className="flex items-center justify-between"><span className="text-xs font-medium text-slate-800 truncate">{e.name}</span><span className="w-2 h-2 rounded-full" style={{ background: e.health === "critical" ? "var(--signal)" : e.health === "warning" ? "#d4a017" : "var(--ai)" }} /></div>
                  <div className="font-heading text-lg font-semibold tabular mt-1">{fmt$(e.pipeline_value)}</div>
                  <div className="text-[10px] text-slate-500">{e.overdue_tasks} overdue · {e.low_stock_items} low stock</div>
                </button>
              ))}
            </div>
          </>) : (<>
            <Head icon={Rocket} title="Missions in flight" count={missions.length} to="/missions" onGo={navigate} />
            <div className="mt-3 space-y-2">
              {missions.length === 0 && <Empty text="No missions yet. Ask Atlas to plan one from an objective." />}
              {missions.slice(0, 3).map((m) => <MissionRow key={m.id} m={m} onClick={() => navigate("/missions")} />)}
            </div>
          </>)}
        </section>

        {hasEntities && missions.length > 0 && (
          <section className="lg:col-span-6 panel p-5 rise rise-5" data-testid="home-missions">
            <Head icon={Rocket} title="Missions in flight" count={missions.length} to="/missions" onGo={navigate} />
            <div className="mt-3 space-y-2">{missions.slice(0, 3).map((m) => <MissionRow key={m.id} m={m} onClick={() => navigate("/missions")} />)}</div>
          </section>
        )}

        <section className={`panel p-5 rise rise-5 ${hasEntities && missions.length > 0 ? "lg:col-span-6" : "lg:col-span-12"}`} data-testid="home-activity">
          <Head icon={Activity} title="Business activity" to="/admin/audit" onGo={navigate} />
          <div className="mt-3 divide-y" style={{ borderColor: "var(--line)" }}>
            {activity.map((a) => (
              <div key={a.id} className="py-2 flex items-center gap-3 text-sm"><Radio className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" /><span className="flex-1 text-slate-700 truncate">{a.summary}</span><span className="text-[11px] text-slate-400 whitespace-nowrap">{a.actor_name || "Atlas"} · {new Date(a.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span></div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

const Head = ({ icon: I, title, count, to, onGo }) => (
  <div className="flex items-center justify-between">
    <h2 className="font-heading font-semibold text-slate-900 flex items-center gap-2 text-base"><I className="w-4 h-4 text-slate-400" /> {title}{count != null && <span className="chip border-slate-200 text-slate-600 tabular">{count}</span>}</h2>
    {to && <button onClick={() => onGo(to)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1">Open <ArrowUpRight className="w-3.5 h-3.5" /></button>}
  </div>
);
const Empty = ({ text }) => <p className="text-sm text-slate-400 px-3 py-4">{text}</p>;
const BriefList = ({ title, items, color }) => items?.length ? (
  <div><p className="eyebrow-light mb-1.5" style={{ color }}>{title}</p><ul className="space-y-1.5">{items.slice(0, 4).map((t, i) => <li key={i} className="text-[13px] text-slate-200 flex gap-2"><span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: color }} />{t}</li>)}</ul></div>
) : null;

export function MissionRow({ m, onClick }) {
  const done = m.steps.filter((s) => s.status === "done").length;
  return (
    <button onClick={onClick} data-testid="mission-row" className="w-full text-left px-3 py-2.5 rounded-xl border row-hover" style={{ borderColor: "var(--line)" }}>
      <div className="flex items-center justify-between gap-2"><span className="text-sm font-medium text-slate-800 truncate">{m.name}</span><span className="chip border-slate-200 text-slate-500 uppercase">{m.status}</span></div>
      <div className="mt-2 h-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${(done / Math.max(1, m.steps.length)) * 100}%`, background: "var(--ai)" }} /></div>
      <div className="text-[11px] text-slate-500 mt-1">{done}/{m.steps.length} steps · {m.owner_name}</div>
    </button>
  );
}
